import type { LandingPage } from './types'
import { getComboPages } from './combos'

/**
 * Registry of all SEO landing pages. Add a page by appending an entry here —
 * the runtime route (`/pobyty/$slug`), the interlink lists and the build scripts
 * (sitemap, prerender, Amplify rules) all read this registry directly.
 *
 * Region slugs must match the backend `/enum` `regions[].slug`. Facility keys
 * must match a camelCase key of `LocationFacilitySchema` or a leaf of
 * `UnitFacilitySchema`'s sections in `src/api/accommodation/types.ts`.
 */

const regionPages: LandingPage[] = [
  {
    slug: 'stredni-cechy',
    kind: 'region',
    h1: 'Ubytování – Střední Čechy',
    linkLabel: 'Střední Čechy',
    metaTitle: 'Střední Čechy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Střední Čechy nabídnou výlety za historií i přírodou na dosah Prahy – od Karlštejna po Kokořínsko. Vyberte si z právě uvolněných termínů chalup, chat i penzionů a vyrazte na spontánní víkend bez dlouhého plánování.',
    filter: { region: 'stredni-cechy' },
    about: {
      heading: 'Víkend ve středních Čechách',
      paragraphs: [
        'Střední Čechy jsou nejbližší únik z Prahy – za hodinu jízdy jste na Křivoklátsku, v Brdech nebo u Sázavy a nemusíte řešit dlouhou cestu ani velký plán. Hodí se proto přesně na spontánní víkend, kdy se rozhodnete ve čtvrtek a v pátek odpoledne už vybalujete.',
        'Na výlety je tu nejvíc hradů a zámků v republice: Karlštejn, Křivoklát, Konopiště nebo Kokořín. Kdo chce víc přírody, vyrazí na brdské hřebeny, kolem meandrů Berounky nebo ke Slapské přehradě. Na kolo jsou ideální roviny Polabí, s dětmi zase Kutná Hora a její stříbrné doly.',
        'V nabídce najdete hlavně chalupy a chaty pro rodiny i party a pár menších glampingů. Majitelé sem dávají termíny, které se jim uvolnily nebo neobsadily, a protože jsou střední Čechy oblíbené, volná místa na víkend mizí rychle – vyplatí se sem kouknout v týdnu.',
      ],
    },
  },
  {
    slug: 'jizni-cechy',
    kind: 'region',
    h1: 'Ubytování – Jižní Čechy',
    linkLabel: 'Jižní Čechy',
    metaTitle: 'Jižní Čechy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Rybníky, renesanční města a klidná krajina Třeboňska i Lipenska – jižní Čechy jsou stvořené pro pomalou dovolenou. Prohlédněte si aktuální last-minute nabídky ubytování a odjeďte třeba už tento víkend.',
    filter: { region: 'jizni-cechy' },
    about: {
      heading: 'Jižní Čechy na poslední chvíli',
      paragraphs: [
        'Jižní Čechy jsou krajem rybníků, hrází a malých měst s renesančními náměstími. Na Třeboňsku se jezdí na kole po hrázích kolem Rožmberka, v Českém Krumlově a Holašovicích na vás dýchne památka UNESCO a u Lipna nebo na Vltavě strávíte léto na vodě.',
        'Krajina je tu rovinatější a klidnější než na horách, takže se hodí pro rodiny s dětmi, cyklisty i pro ty, kdo si chtějí hlavně odpočinout. Na jaře a na podzim stojí za to výlovy rybníků, v létě koupání a splouvání Vltavy, mimo sezonu prázdné cyklotrasy a klidné hospody.',
        'Ubytování v jižních Čechách sahá od rekonstruovaných statků a chalup po chatky u vody a glampingy. Termíny tu průběžně přidávají sami majitelé, takže nabídka se mění každý den – ukazujeme vždy jen to, co je opravdu volné.',
      ],
    },
  },
  {
    slug: 'sumava',
    kind: 'region',
    h1: 'Ubytování – Šumava',
    linkLabel: 'Šumava',
    metaTitle: 'Šumava – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Hluboké lesy, slatě a čisté řeky Šumavy lákají na túry v každém ročním období. Mezi právě uvolněnými termíny najdete útulné chalupy i penziony kousek od nejkrásnějších šumavských tras.',
    filter: { region: 'sumava' },
    about: {
      heading: 'Šumava – kdy jet a kde hledat ubytování',
      paragraphs: [
        'Šumava je největší souvislý les ve střední Evropě a zároveň náš největší národní park. Čeká vás Boubínský prales, ledovcová jezera jako Plešné nebo Černé, rašelinné slatě s povalovými chodníky a údolí Vydry, kde se jde kilometry podél divoké řeky.',
        'V létě se sem jezdí na pěší túry a kolo – rovinaté pláně kolem Kvildy a Modravy zvládnou i děti. V zimě se Šumava mění v běžkařský ráj se stovkami kilometrů upravených stop a menšími sjezdovkami na Zadově nebo Špičáku. Podzim je tu nejklidnější a nejbarevnější.',
        'Ubytování je rozeseté po malých obcích, často na samotách bez signálu – přesně to, proč sem lidi jezdí. Mezi last-minute nabídkami najdete roubenky, horské chalupy i penziony a vždy jen termíny, které majitel právě uvolnil.',
      ],
    },
  },
  {
    slug: 'krusne-hory',
    kind: 'region',
    h1: 'Ubytování – Krušné hory',
    linkLabel: 'Krušné hory',
    metaTitle: 'Krušné hory – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Krušné hory spojují nenáročné hřebenovky v létě s běžkařskými stopami a sjezdovkami v zimě. Sledujte last-minute termíny ubytování a využijte klidnějších, dostupnějších hor na české straně hranice.',
    filter: { region: 'krusne-hory' },
    about: {
      heading: 'Krušné hory – klidnější hory na dosah',
      paragraphs: [
        'Krušné hory jsou dlouhý náhorní hřbet podél hranic se Saskem. Nejvyšší je Klínovec, kousek od něj Boží Dar, nejvýše položené město ve střední Evropě. Hory jsou mírné a otevřené, takže se tu dobře chodí, jezdí na kole i na běžkách.',
        'V zimě vede po hřebenech Krušnohorská lyžařská magistrála a sjezdovky na Klínovci patří k nejlepším v Česku. V létě láká Božídarské rašeliniště, rozhledny a hornické památky – Hornický region Krušnohoří je od roku 2019 zapsaný na seznamu UNESCO. Jáchymov navíc nabízí radonové lázně.',
        'Oproti Krkonoším tu bývá méně lidí a příznivější ceny. V nabídce najdete horské chaty a chalupy, apartmány i ubytování s wellness, často kousek od stopy nebo sjezdovky.',
      ],
    },
  },
  {
    slug: 'krkonose',
    kind: 'region',
    h1: 'Ubytování – Krkonoše',
    linkLabel: 'Krkonoše',
    metaTitle: 'Krkonoše – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Nejvyšší české hory nabízejí Sněžku, vodopády i rozlehlé lyžařské areály. Projděte aktuální volné termíny chalup a apartmánů v Krkonoších a vyrazte na hory bez měsíce dopředu placené rezervace.',
    filter: { region: 'krkonose' },
    about: {
      heading: 'Krkonoše – ubytování v nejvyšších českých horách',
      paragraphs: [
        'Krkonoše jsou nejvyšší a nejnavštěvovanější hory v Česku. Na Sněžku (1 603 m) se dá vyjít pěšky nebo vyjet lanovkou z Pece, v Labském dole najdete pramen Labe a Labský vodopád, u Harrachova Mumlavský vodopád. Krkonošský národní park je nejstarší v republice.',
        'V zimě se lyžuje ve Špindlerově Mlýně, Peci pod Sněžkou, Harrachově nebo Rokytnici a po hřebenech vedou běžkařské trasy. V létě jsou Krkonoše plné turistických a cyklistických cest pro všechny úrovně a s dětmi můžete vyrazit na Stezku korunami stromů v Janských Lázních.',
        'Ubytování v Krkonoších se v sezoně obsazuje dlouho dopředu, a proto se vyplatí sledovat last-minute termíny – majitelé chalup a apartmánů sem dávají místa, která se jim na poslední chvíli uvolnila. Je to nejjednodušší cesta, jak se dostat na hory bez rezervace na půl roku dopředu.',
      ],
    },
  },
  {
    slug: 'jizerske-hory',
    kind: 'region',
    h1: 'Ubytování – Jizerské hory',
    linkLabel: 'Jizerské hory',
    metaTitle: 'Jizerské hory – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Jizerské hory jsou rájem běžkařů a cyklistů s hustou sítí upravovaných tras. Mezi last-minute nabídkami objevíte roubenky i penziony blízko Jizerské magistrály.',
    filter: { region: 'jizerske-hory' },
    about: {
      heading: 'Jizerské hory – na běžky, kolo i pěšky',
      paragraphs: [
        'Jizerské hory jsou známé hlavně Jizerskou magistrálou – sítí upravovaných běžkařských tras, která patří k nejlepším ve střední Evropě. Startuje se z Bedřichova, Josefova Dolu nebo Smědavy a z hřebenů jsou výhledy až do Polska.',
        'Mimo zimu jsou hory stejně příjemné: rozlehlé lesy, rašeliniště na Jizerce, vodopády Černého potoka u Bílého Potoka a desítky rozhleden od Štěpánky po Královku. Na severní straně rostou Jizerskohorské bučiny, které patří k přírodnímu dědictví UNESCO.',
        'Jizerky jsou blízko Prahy i Liberce, takže se sem dá vyrazit i jen na prodloužený víkend. V nabídce najdete hlavně roubenky, chalupy a horské apartmány – vždy s termíny, které jsou právě teď volné.',
      ],
    },
  },
  {
    slug: 'cesky-raj-a-kokorinsko',
    kind: 'region',
    h1: 'Ubytování – Český ráj a Kokořínsko',
    linkLabel: 'Český ráj a Kokořínsko',
    metaTitle: 'Český ráj a Kokořínsko – last-minute ubytování | Mátevolno',
    perex:
      'Skalní města, hrady a vyhlídky Českého ráje a Kokořínska patří k nejfotogeničtějším koutům Česka. Vyberte si z právě uvolněných termínů ubytování a vyrazte na den plný skal a rozhleden.',
    filter: { region: 'cesky-raj-a-kokorinsko' },
    about: {
      heading: 'Český ráj a Kokořínsko – skály, hrady a vyhlídky',
      paragraphs: [
        'Český ráj je nejstarší chráněná krajinná oblast v Česku a geopark UNESCO. Pískovcová skalní města jako Prachovské skály nebo Hruboskalsko jsou protkaná stezkami s vyhlídkami, nad krajinou se tyčí zřícenina Trosky a mezi skalami stojí hrady Kost a Valdštejn.',
        'Kokořínsko, oficiálně CHKO Kokořínsko – Máchův kraj, nabízí podobnou krajinu v klidnějším vydání: hluboká údolí, skalní útvary Pokličky, hrad Kokořín a na severu Máchovo jezero na koupání. Oba kraje jsou kousek od Prahy a hodí se na výlety s dětmi i na delší pěší túry.',
        'Vybírat můžete z chalup, roubenek i glampingů, často s výhledem do krajiny nebo přímo pod skalami. Termíny se mění každý den – ukazujeme jen ty, které jsou aktuálně volné.',
      ],
    },
  },
  {
    slug: 'orlicke-hory',
    kind: 'region',
    h1: 'Ubytování – Orlické hory',
    linkLabel: 'Orlické hory',
    metaTitle: 'Orlické hory – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Orlické hory zůstávají příjemně nezaplněné – ideální pro klidné túry i rodinné lyžování. Podívejte se na aktuální last-minute ubytování a užijte si hory po svém.',
    filter: { region: 'orlicke-hory' },
    about: {
      heading: 'Orlické hory – hory bez davů',
      paragraphs: [
        'Orlické hory jsou nižší a tišší sestrou Krkonoš. Nejvyšší vrchol Velká Deštná měří 1 115 metrů, hřebenová cesta vede přes Šerlich s Masarykovou chatou a krajinu doplňují předválečné pevnosti, například dělostřelecká tvrz Hanička.',
        'V zimě jsou tu rodinná lyžařská střediska v Deštném, Říčkách nebo Zdobnici a dobré podmínky pro běžky. V létě se po hřebenech chodí a jezdí na kole a dole v údolích najdete klid a malé vesnice, kde se zastavil čas.',
        'Orlické hory si vybírají lidé, kteří nechtějí stát frontu na lanovku ani hledat místo v hospodě. Mezi last-minute nabídkami najdete chalupy a chaty pro rodiny i větší party.',
      ],
    },
  },
  {
    slug: 'vysocina',
    kind: 'region',
    h1: 'Ubytování – Vysočina',
    linkLabel: 'Vysočina',
    metaTitle: 'Vysočina – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Vrchoviny, rybníky a památky UNESCO dělají z Vysočiny klidnou destinaci pro celý rok. Mezi volnými termíny najdete chalupy i penziony daleko od davů, přesto snadno dostupné z celé republiky.',
    filter: { region: 'vysocina' },
    about: {
      heading: 'Vysočina – klid uprostřed republiky',
      paragraphs: [
        'Vysočina leží na půli cesty mezi Prahou a Brnem a je ideální pro ty, kdo chtějí vypnout a nemít daleko. Krajina plná lesů, luk a rybníků se nejlépe poznává pěšky nebo na kole, třeba ve Žďárských vrších kolem skalních útvarů Devět skal.',
        'Na Vysočině jsou hned tři památky UNESCO: kostel sv. Jana Nepomuckého na Zelené hoře u Žďáru nad Sázavou, historické centrum Telče a židovská čtvrť s bazilikou v Třebíči. V zimě se dá běžkovat kolem Nového Města na Moravě, v létě koupat v rybnících.',
        'Ubytování je tu hlavně na chalupách, statcích a chatách v malých obcích. Vybírejte z termínů, které majitelé právě uvolnili – na Vysočinu se dá vyrazit klidně i v pátek odpoledne.',
      ],
    },
  },
  {
    slug: 'ceske-svycarsko',
    kind: 'region',
    h1: 'Ubytování – České Švýcarsko',
    linkLabel: 'České Švýcarsko',
    metaTitle: 'České Švýcarsko – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Pravčická brána, soutěsky a pískovcová skalní města patří k nejdivočejší přírodě v Česku. Sledujte právě uvolněné termíny ubytování na hraně národního parku a vyrazte za nejznámějšími vyhlídkami.',
    filter: { region: 'ceske-svycarsko' },
    about: {
      heading: 'České Švýcarsko – pískovcová divočina',
      paragraphs: [
        'Národní park České Švýcarsko patří k nejvýraznějším krajinám v Česku. Pravčická brána je největší přirozená skalní brána v Evropě, v soutěskách Kamenice se jezdí na loďkách mezi skalními stěnami a z vyhlídek nad Jetřichovicemi je vidět daleko do Saska.',
        'Kraj se nejlépe poznává pěšky – trasy jsou krátké, ale členité, se schody a žebříky, takže je zvládnou i starší děti. V okolí je navíc Děčínský Sněžník, Tiské stěny nebo Labské údolí s cyklostezkou podél řeky.',
        'Ubytování v Českém Švýcarsku je spíš menší a komornější – chalupy, chaty a penziony v obcích na okraji parku. Mimo hlavní sezonu tu bývá nečekaně klid, a právě tehdy se uvolňuje nejvíc last-minute termínů.',
      ],
    },
  },
  {
    slug: 'jizni-a-stredni-morava',
    kind: 'region',
    h1: 'Ubytování – Jižní a střední Morava',
    linkLabel: 'Jižní a střední Morava',
    metaTitle: 'Jižní a střední Morava – last-minute ubytování | Mátevolno',
    perex:
      'Vinice, cyklostezky a folklor jižní a střední Moravy zvou k pohodovým výletům s dobrým vínem. Prohlédněte si aktuální last-minute nabídky ubytování ve vinařských obcích i historických městech.',
    filter: { region: 'jizni-a-stredni-morava' },
    about: {
      heading: 'Jižní a střední Morava – víno, kola a památky',
      paragraphs: [
        'Jižní Morava je krajem vinic, sklepů a vinařských obcí. Pálava s Mikulovem, Lednicko-valtický areál zapsaný v UNESCO a Znojemsko s Národním parkem Podyjí patří k nejoblíbenějším cílům na prodloužený víkend – ideálně na kole po vinařských stezkách.',
        'Střední Morava přidává Moravský kras s propastí Macocha a Punkevními jeskyněmi, zámek a zahrady v Kroměříži nebo zvlněnou krajinu Moravského Toskánska. Na Slovácku zase zažijete folklor, krojované hody a Baťův kanál, po kterém se dá plout na lodi.',
        'Vybírat můžete z apartmánů ve vinařských obcích, chalup, glampingů i ubytování u vinařů. Nejvíc volných termínů se objevuje mimo vinobraní a velké akce – ukazujeme jen ty, které jsou právě teď volné.',
      ],
    },
  },
  {
    slug: 'jeseniky',
    kind: 'region',
    h1: 'Ubytování – Jeseníky',
    linkLabel: 'Jeseníky',
    metaTitle: 'Jeseníky – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Praděd, lázně a čistý horský vzduch dělají z Jeseníků skvělou volbu pro léto i zimu. Mezi volnými termíny objevíte chaty i wellness penziony v nejvyšších moravských horách.',
    filter: { region: 'jeseniky' },
    about: {
      heading: 'Jeseníky – nejvyšší hory Moravy',
      paragraphs: [
        'Jeseníky vrcholí Pradědem (1 491 m), na který vede asfaltová cesta z Ovčárny i řada turistických tras. Z hřebenů jsou dlouhé výhledy, v lesích najdete vodopády na Bílé Opavě a na Rejvízu rašeliniště s Velkým mechovým jezírkem.',
        'Jeseníky jsou také krajem lázní – Lázně Jeseník založil Vincenz Priessnitz, Karlova Studánka se chlubí jedním z nejčistších vzduchů ve střední Evropě. V zimě se lyžuje na Ramzové, Červenohorském sedle nebo na Dolní Moravě, v létě chodí a jezdí na kole.',
        'Daleko od velkých měst tu najdete klid a příznivější ceny než v Krkonoších. V nabídce jsou horské chaty, chalupy i penziony s wellness – vždy s termíny, které jsou zrovna volné.',
      ],
    },
  },
  {
    slug: 'beskydy-a-valassko',
    kind: 'region',
    h1: 'Ubytování – Beskydy a Valašsko',
    linkLabel: 'Beskydy a Valašsko',
    metaTitle: 'Beskydy a Valašsko – last-minute ubytování | Mátevolno',
    perex:
      'Beskydy a Valašsko lákají na hřebenovky, dřevěnou architekturu a valašskou pohostinnost. Vyberte si z právě uvolněných termínů ubytování a poznejte hory s vlastním svérázem.',
    filter: { region: 'beskydy-a-valassko' },
    about: {
      heading: 'Beskydy a Valašsko – hory se svérázem',
      paragraphs: [
        'Moravskoslezské Beskydy lákají na hřebenovky s výhledy: Lysá hora je nejvyšší vrchol, Radhošť s kaplí a Pustevny s dřevěnými stavbami Dušana Jurkoviče patří k nejznámějším místům. Kolem vedou desítky turistických i cyklistických tras.',
        'Valašsko je známé dřevěnými kostely, roubenkami a pohostinností. V Rožnově pod Radhoštěm stojí Valašské muzeum v přírodě, ve Velkých Karlovicích a na Horní Bečvě se v zimě lyžuje a v létě koupe v přehradě. Kraj si zachoval vlastní tradice, kuchyni i nářečí.',
        'Ubytování v Beskydech je typicky v roubenkách a chalupách na kopcích, často s výhledem a vlastní saunou. Mezi last-minute nabídkami najdete místa pro páry, rodiny i velké party.',
      ],
    },
  },
  {
    slug: 'oderske-vrchy',
    kind: 'region',
    h1: 'Ubytování – Oderské vrchy',
    linkLabel: 'Oderské vrchy',
    metaTitle: 'Oderské vrchy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Oderské vrchy jsou tichým koutem pro ty, kdo hledají klid, lesy a prázdné cyklostezky. Mezi last-minute nabídkami najdete ubytování daleko od turistického ruchu.',
    filter: { region: 'oderske-vrchy' },
    about: {
      heading: 'Oderské vrchy – tichý kout Moravy',
      paragraphs: [
        'Oderské vrchy jsou mírně zvlněná vrchovina mezi Olomoucí a Ostravou, kde pramení řeka Odra. Krajina lesů, luk a hlubokých údolí je chráněná jako přírodní park a turisté sem zatím jezdí jen málo – o to víc klidu tu najdete.',
        'Na výlety je tu Potštátské skalní město, údolí Odry s meandry, zámky v Potštátě nebo Fulneku a dlouhé cyklotrasy po málo frekventovaných silnicích. V okolí je i Moravská brána a Poodří s rybníky a lužními lesy.',
        'Ubytování je tu hlavně na chalupách a samotách. Pokud hledáte víkend bez lidí, Oderské vrchy jsou dobrou volbou – ukazujeme jen termíny, které jsou aktuálně volné.',
      ],
    },
  },
  {
    slug: 'zapadni-cechy',
    kind: 'region',
    h1: 'Ubytování – Západní Čechy',
    linkLabel: 'Západní Čechy',
    metaTitle: 'Západní Čechy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Lázeňský trojúhelník, Slavkovský les i Český les – západní Čechy spojují wellness s přírodou. Sledujte aktuální volné termíny ubytování a spojte odpočinek s objevováním.',
    filter: { region: 'zapadni-cechy' },
    about: {
      heading: 'Západní Čechy – lázně a příroda',
      paragraphs: [
        'Západní Čechy jsou proslulé lázeňským trojúhelníkem: Karlovy Vary, Mariánské Lázně a Františkovy Lázně patří mezi Slavné lázně Evropy zapsané v UNESCO. Kolonády, prameny a lázeňské parky se dají spojit s procházkami po okolních lesích.',
        'Kdo chce víc přírody, vyrazí do Slavkovského lesa, do Českého lesa podél hranic nebo do přírodní rezervace Soos s bahenními sopkami. Nádherné je také historické městečko Loket nad Ohří nebo Cheb s chebským hradem.',
        'Vybírat můžete z chalup, statků a apartmánů – na wellness víkend i na aktivní dovolenou. Volné termíny se v západních Čechách objevují průběžně, nejvíc mimo letní prázdniny.',
      ],
    },
  },
  {
    slug: 'luzicke-hory',
    kind: 'region',
    h1: 'Ubytování – Lužické hory',
    linkLabel: 'Lužické hory',
    metaTitle: 'Lužické hory – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Sopečné kužely, rozhledny a skalní vyhlídky Lužických hor jsou ideální na prodloužený víkend. Mezi právě uvolněnými termíny najdete roubenky i penziony v klidném příhraničí.',
    filter: { region: 'luzicke-hory' },
    about: {
      heading: 'Lužické hory – rozhledny a podstávkové domy',
      paragraphs: [
        'Lužické hory jsou malé pohoří na severu Čech s výraznými znělcovými kopci. Nejvyšší je Luž, ale nejoblíbenější je Jedlová s rozhlednou, Klíč s výhledem do celého kraje a skalní hrad Sloup. Krátké výstupy a dlouhé výhledy dělají z Lužických hor ideální cíl pro rodiny.',
        'Typické jsou podstávkové domy – roubené stavby s dřevěnou konstrukcí kolem přízemí, které najdete třeba v Krompachu nebo Mařenicích. V zimě se tu běžkuje a lyžuje na menších svazích, v létě chodí a jezdí na kole po lesních cestách.',
        'Řada podstávkových domů dnes slouží jako stylové chalupy k pronájmu. Mezi last-minute nabídkami najdete ubytování v klidném příhraničí, kousek od Českého Švýcarska i Žitavských hor.',
      ],
    },
  },
  {
    slug: 'vychodni-cechy',
    kind: 'region',
    h1: 'Ubytování – Východní Čechy',
    linkLabel: 'Východní Čechy',
    metaTitle: 'Východní Čechy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Od Adršpachu po Orlické hory nabízejí východní Čechy skály, hory i historická města. Projděte si last-minute ubytování a naplánujte výlet za jedním z nejrozmanitějších koutů republiky.',
    filter: { region: 'vychodni-cechy' },
    about: {
      heading: 'Východní Čechy – skály, zámky a hory',
      paragraphs: [
        'Východní Čechy jsou rozmanité: Adršpašsko-teplické skály a Broumovské stěny patří k nejpůsobivějším skalním městům v Evropě, Babiččino údolí v Ratibořicích zná každý ze školy a barokní Kuks s Braunovými sochami je jedinečný.',
        'Na výlety jsou tu i zámek Litomyšl zapsaný v UNESCO, Hradec Králové, Pardubice nebo Opočno. Na sever kraj přechází do Orlických hor a Krkonoš, takže se dá jeden víkend strávit na skalách a další na hřebenech.',
        'V nabídce najdete chalupy, roubenky i glampingy na Broumovsku a v Podkrkonoší. Termíny se mění každý den – ukazujeme jen ty, které majitel právě teď nabízí.',
      ],
    },
  },
  {
    slug: 'praha',
    kind: 'region',
    h1: 'Ubytování – Praha',
    linkLabel: 'Praha',
    metaTitle: 'Praha – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Praha se dá objevovat pořád dokola – historické centrum, kavárny i skrytá zákoutí. Mezi právě uvolněnými termíny najdete ubytování pro spontánní městský víkend v srdci Česka.',
    filter: { region: 'praha' },
    about: {
      heading: 'Ubytování v Praze na poslední chvíli',
      paragraphs: [
        'Praha se dá navštívit pořád znovu: Pražský hrad, Malá Strana a Staroměstské náměstí jsou povinnost, ale stejně zajímavé jsou čtvrti jako Vinohrady, Karlín nebo Holešovice s kavárnami, galeriemi a náplavkou podél Vltavy.',
        'Na víkend v Praze se hodí i méně známá místa – Vyšehrad, Petřín, Stromovka nebo Divoká Šárka, kde si odpočinete od davů. Městská doprava jezdí v noci, takže se dá ubytovat i dál od centra.',
        'Mezi last-minute nabídkami najdete ubytování v Praze a nejbližším okolí – od netradičních míst po klidné domy na okraji města. Termíny se uvolňují průběžně, a když se rozhodnete na poslední chvíli, ušetříte dlouhé hledání.',
      ],
    },
  },
  {
    slug: 'zahranici',
    kind: 'region',
    h1: 'Ubytování – Zahraničí',
    linkLabel: 'Zahraničí',
    metaTitle: 'Zahraničí – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Někdy má smysl vyrazit i za hranice – k moři, do hor nebo za sluncem. Sledujte právě uvolněné zahraniční termíny a chyťte dovolenou na poslední chvíli za výhodnou cenu.',
    filter: { region: 'zahranici' },
    about: {
      heading: 'Last-minute ubytování v zahraničí',
      paragraphs: [
        'Většina nabídky Mátevolno je v Česku, ale postupně přibývají i místa za hranicemi – hlavně v sousedních zemích, kam se dá dojet autem za pár hodin. Výhodou je, že na rozdíl od zájezdů si sami určíte, kdy a na jak dlouho jedete.',
        'Podobně jako u českých míst jde o soukromé chalupy, chaty a menší ubytování, jejichž majitelé nabízejí termíny, které se jim uvolnily. Rezervujete přímo u nás, platíte online a o vše další se postará majitel.',
        'Zahraničních nabídek je zatím méně a mění se rychle, takže se vyplatí sem občas nahlédnout. Pokud zrovna nic nenajdete, podívejte se na hory nebo jižní Moravu – i tam se dá zažít dovolená jako v cizině.',
      ],
    },
  },
]

const facilityPages: LandingPage[] = [
  {
    slug: 'se-saunou',
    kind: 'facility',
    h1: 'Ubytování se saunou',
    linkLabel: 'Se saunou',
    metaTitle: 'Ubytování se saunou – privátní wellness | Mátevolno',
    perex:
      'Vlastní sauna promění každý pobyt ve wellness. Vybírejte z právě uvolněných termínů ubytování se saunou – od horských chalup po penziony, kde se po túře prohřejete jen pro sebe.',
    filter: { facility: 'sauna' },
    about: {
      heading: 'Jak vybrat ubytování se saunou',
      paragraphs: [
        'Sauna na chalupě je nejjednodušší cesta k wellness víkendu bez davů. Na rozdíl od hotelových wellness ji máte jen pro sebe a své lidi, saunujete kdy chcete a mezi saunováním vyběhnete do zahrady, do sněhu nebo do studené kádě.',
        'Při výběru se vyplatí zkontrolovat, jestli je sauna v ceně, nebo se doplácí, a jestli je finská, infra nebo venkovní sudová. U větších skupin se podívejte na velikost – do menší sauny se vejdou dva až čtyři lidé. Pokud chcete opravdu soukromí, hledejte samoty nebo místa s jedinou jednotkou.',
        'Ubytování se saunou je nejžádanější v zimě a na podzim, ale termíny se uvolňují i tehdy. Majitelé sem dávají volná místa průběžně, takže se vyplatí nabídku sledovat a vyrazit, jakmile se něco objeví.',
      ],
    },
  },
  {
    slug: 's-bazenem',
    kind: 'facility',
    h1: 'Ubytování s bazénem',
    linkLabel: 'S bazénem',
    metaTitle: 'Ubytování s bazénem – vnitřní i venkovní | Mátevolno',
    perex:
      'Bazén oceníte v horkých dnech i za deště. Prohlédněte si aktuální last-minute nabídky ubytování s bazénem a užijte si osvěžení bez cesty ke koupališti.',
    filter: { facility: 'pool' },
    about: {
      heading: 'Ubytování s bazénem – na co se dívat',
      paragraphs: [
        'Bazén u ubytování ocení hlavně rodiny s dětmi a party v létě – nemusíte nikam jezdit, plavky stačí vytáhnout z tašky. Některá místa mají vnitřní nebo krytý bazén, který funguje i mimo sezonu.',
        'U venkovních bazénů se vyplatí ověřit, od kdy do kdy jsou napuštěné, jestli jsou vyhřívané a zda je bazén jen pro vás, nebo ho sdílíte s dalšími hosty. Podrobnosti najdete v popisu každého místa, případně se můžete zeptat majitele.',
        'Na Mátevolno najdete jen termíny, které jsou právě teď volné – často i v létě, kdy se u ubytování s bazénem obvykle obsazuje všechno dlouho dopředu.',
      ],
    },
  },
  {
    slug: 's-virivkou',
    kind: 'facility',
    h1: 'Ubytování s vířivkou',
    linkLabel: 'S vířivkou',
    metaTitle: 'Ubytování s vířivkou – relax po celý rok | Mátevolno',
    perex:
      'Vířivka je zárukou odpočinku v jakémkoli počasí. Mezi právě uvolněnými termíny najdete chalupy i penziony s privátní vířivkou pro pohodový romantický i rodinný pobyt.',
    filter: { facility: 'whirlpool' },
    about: {
      heading: 'Ubytování s vířivkou pro odpočinek',
      paragraphs: [
        'Vířivka je jednoduchý luxus: po celodenním výletu nebo běžkách se v teplé vodě dokonale uvolníte. Na chalupách bývá venkovní na terase s výhledem nebo vnitřní v koupelně, kterou využijete za jakéhokoli počasí.',
        'Při výběru si zkontrolujte, jestli je vířivka v ceně, kolik lidí se do ní vejde a jestli je k dispozici celoročně. Venkovní vířivka v zimě pod hvězdami patří k nejsilnějším zážitkům, proto bývají tyto termíny rychle pryč.',
        'Ubytování s vířivkou je oblíbené hlavně na romantické pobyty pro dva a na relaxační víkendy s přáteli. Sledujte nabídku – majitelé sem přidávají volné termíny průběžně.',
      ],
    },
  },
  {
    slug: 's-kadi',
    kind: 'facility',
    h1: 'Ubytování s venkovní vířivkou (kádí)',
    linkLabel: 'S venkovní kádí',
    metaTitle: 'Ubytování s venkovní kádí či vířivkou | Mátevolno',
    perex:
      'Horká lázeň pod hvězdami má neopakovatelnou atmosféru. Sledujte last-minute termíny ubytování s venkovní vířivkou nebo dřevěnou kádí a dopřejte si relax přímo v přírodě.',
    filter: { facility: 'hotTub' },
    about: {
      heading: 'Venkovní káď – horká lázeň pod širým nebem',
      paragraphs: [
        'Dřevěná káď, kterou si sami roztopíte ohněm, je jiný zážitek než elektrická vířivka. Voda se ohřívá pomalu, sedíte v ní venku za každého počasí a v zimě je největší kouzlo vylézt do sněhu a zase se ponořit.',
        'Káď bývá potřeba roztopit několik hodin předem – majitel vám obvykle ukáže, jak na to, nebo ji připraví sám. V popisu ubytování zkontrolujte, jestli je v ceně, nebo se platí zvlášť, a kolik lidí se do ní vejde.',
        'Ubytování s venkovní kádí najdete hlavně na horských chalupách, samotách a v glampinzích. Ukazujeme jen termíny, které jsou právě teď volné, takže se můžete rozhodnout na poslední chvíli.',
      ],
    },
  },
  {
    slug: 's-grilem',
    kind: 'facility',
    h1: 'Ubytování s grilem',
    linkLabel: 'S grilem',
    metaTitle: 'Ubytování s grilem a venkovním posezením | Mátevolno',
    perex:
      'Společné grilování patří k létu na chalupě neodmyslitelně. Vyberte si z právě uvolněných termínů ubytování s grilem a venkovním posezením pro celou partu i rodinu.',
    filter: { facility: 'grill' },
    about: {
      heading: 'Ubytování s grilem a posezením venku',
      paragraphs: [
        'Gril na zahradě patří k chalupě stejně jako krb – večer se všichni sejdou kolem ohně a nikdo nemusí stát u sporáku. Hodí se pro rodiny i větší party, které chtějí trávit čas venku.',
        'Některá místa mají plynový gril, jiná zahradní krb nebo ohniště, kde se dá opékat. Podívejte se do popisu, jestli je k dispozici dřevo nebo uhlí a jestli je posezení kryté – pak si grilování užijete i za deště.',
        'Ubytování s grilem je nejvíc žádané od jara do podzimu. Vybírejte z aktuálních last-minute nabídek a vyrazte, jakmile vyjde počasí.',
      ],
    },
  },
  {
    slug: 's-krbem',
    kind: 'facility',
    h1: 'Ubytování s krbem',
    linkLabel: 'S krbem',
    metaTitle: 'Ubytování s krbem – útulné večery u ohně | Mátevolno',
    perex:
      'Praskající oheň v krbu udělá z chladného večera zážitek. Mezi last-minute nabídkami objevíte chalupy a roubenky s krbem, kde se zahřejete po celodenním výletu.',
    filter: { facility: 'indoorFireplace' },
    about: {
      heading: 'Ubytování s krbem pro chladné večery',
      paragraphs: [
        'Krb dělá z chalupy domov. Po celodenní túře nebo lyžování nic nepřekoná teplo praskajícího ohně, knihu a hrnek čaje – a pro mnoho lidí je to hlavní důvod, proč jezdí na chalupu i v zimě.',
        'Vybírat můžete z klasických krbů, krbových kamen i kachlových kamen. Zkontrolujte si v popisu, jestli je dřevo v ceně, nebo si ho přivezete, a zda je krb hlavní zdroj tepla – pak počítejte s tím, že se o oheň budete starat.',
        'Ubytování s krbem je nejžádanější od podzimu do jara, ale volné termíny se objevují pořád. Ukazujeme jen ta místa, která jsou právě teď k dispozici.',
      ],
    },
  },
  {
    slug: 'pro-deti',
    kind: 'facility',
    h1: 'Ubytování pro rodiny s dětmi',
    linkLabel: 'Pro rodiny s dětmi',
    metaTitle: 'Ubytování pro děti – hřiště a zázemí pro rodiny | Mátevolno',
    perex:
      'Dětské hřiště a bezpečné zázemí ocení každý rodič. Prohlédněte si právě uvolněné termíny rodinného ubytování, kde se děti vyřádí a vy si odpočinete.',
    filter: { facility: 'childrenPlayground' },
    about: {
      heading: 'Ubytování vhodné pro rodiny s dětmi',
      paragraphs: [
        'S dětmi se vyplatí vybírat ubytování, kde mají kde běhat: zahradu, dětské hřiště, pískoviště nebo houpačky. Vy si pak odpočinete na terase a děti se vyřádí v bezpečí.',
        'Zkontrolujte také dětskou postýlku a židličku, počet ložnic a vybavenou kuchyň, ve které si snadno připravíte jídlo podle svého. U chalup na samotě se podívejte, jestli je pozemek oplocený a jak daleko je nejbližší obchod nebo restaurace.',
        'Rodinné ubytování se v prázdninách obsazuje dlouho dopředu, ale termíny se uvolňují i tehdy. Sledujte last-minute nabídky a vyrazte, když to dětem i vám vyjde.',
      ],
    },
  },
  {
    slug: 'pro-psy',
    kind: 'facility',
    h1: 'Ubytování se psem',
    linkLabel: 'Se psem',
    metaTitle: 'Ubytování se psem – mazlíčci vítáni | Mátevolno',
    perex:
      'Dovolená je lepší, když může jet i pes. Vybírejte z aktuálních last-minute nabídek ubytování, kam smíte se psem, a vyrazte za dobrodružstvím s celou smečkou.',
    filter: { pets: true },
    about: {
      heading: 'Ubytování se psem – mazlíčci vítáni',
      paragraphs: [
        'Dovolená bez psa často znamená shánět hlídání nebo hotel pro zvířata. Ubytování, kam smíte se psem, vám tohle ušetří a pes si výlet užije s vámi – nejlépe na chalupě s oploceným pozemkem a lesem za plotem.',
        'Před rezervací si v popisu ověřte podmínky: kolik psů je povoleno, jestli je pobyt psa zpoplatněný a zda se pes smí pohybovat po celém domě. U velkých plemen se vyplatí zeptat majitele předem.',
        'Ubytování se psem najdete napříč regiony, od hor po jižní Moravu. Ukazujeme jen termíny, které jsou právě teď volné, a filtr na mazlíčky je zapnutý automaticky.',
      ],
    },
  },
]

const categoryPages: LandingPage[] = [
  {
    slug: 'chalupy',
    kind: 'category',
    h1: 'Chalupy',
    linkLabel: 'Chalupy',
    metaTitle: 'Chalupy – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Chalupa je klasika české dovolené – vlastní kuchyně, zahrada a klid bez sousedů za stěnou. Vybírejte z právě uvolněných termínů chalup po celém Česku a vyrazte na venkov třeba už tento víkend.',
    filter: { category: 'chalupa' },
    about: {
      heading: 'Chalupa na poslední chvíli',
      paragraphs: [
        'Chalupa je česká klasika: celý dům jen pro vás, vlastní kuchyně, zahrada a klid bez sousedů za zdí. Hodí se pro rodiny, party přátel i pro dva, kteří chtějí mít prostor a soukromí.',
        'Na Mátevolno najdete chalupy po celém Česku – od roubenek v Krkonoších po statky na Vysočině a domky na jižní Moravě. U každé chalupy uvidíte kapacitu, počet ložnic, vybavení a pravidla pobytu, takže víte předem, do čeho jdete.',
        'Majitelé sem dávají termíny, které se jim uvolnily nebo neobsadily. Díky tomu se dá sehnat chalupa i na oblíbený víkend, kdy jinde už je dávno plno.',
      ],
    },
  },
  {
    slug: 'chaty',
    kind: 'category',
    h1: 'Chaty',
    linkLabel: 'Chaty',
    metaTitle: 'Chaty – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Chata na horách nebo u lesa je ideál pro partu i rodinu, co hledá jednoduchý útěk do přírody. Prohlédněte si aktuální last-minute nabídky chat a sbalte se na poslední chvíli.',
    filter: { category: 'chata' },
    about: {
      heading: 'Chata na víkend',
      paragraphs: [
        'Chata je menší a jednodušší než chalupa, ale přesně v tom je její kouzlo – les za domem, voda kousek od terasy a večer oheň. Je ideální na víkend ve dvou, s rodinou nebo s partou, která nepotřebuje luxus, ale klid a přírodu.',
        'Mezi chatami najdete horské chaty pro lyžaře, chatky u rybníků a přehrad i moderně zařízené chaty se saunou. V popisu každé z nich uvidíte vybavení, kapacitu a dostupnost autem, což se u chat na samotách vyplatí zkontrolovat.',
        'Ukazujeme jen termíny, které jsou právě teď volné. Pokud se rozhodujete na poslední chvíli, je to nejrychlejší cesta, jak najít chatu na tento nebo příští víkend.',
      ],
    },
  },
  {
    slug: 'glamping',
    kind: 'category',
    h1: 'Glamping',
    linkLabel: 'Glamping',
    metaTitle: 'Glamping – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Glamping spojuje pobyt v přírodě s pohodlím pokoje – stany, jurty i designové chatky bez kompromisů. Sledujte právě uvolněné glampingové termíny a spěte pod hvězdami se vším komfortem.',
    filter: { category: 'glamping' },
    about: {
      heading: 'Glamping – příroda s pohodlím',
      paragraphs: [
        'Glamping spojuje spaní v přírodě s pohodlím pokoje: místo spacáku čeká postel, místo kempu soukromí. Spát můžete v jurtě, safari stanu, maringotce, tiny housu nebo v domku na stromě.',
        'Před rezervací se vyplatí zkontrolovat, jestli má glamping vlastní koupelnu a toaletu, nebo je sdílená, jak se topí a jestli je vybavený i na chladnější noci. Mnoho glampingů má vlastní káď nebo saunu, což z nich dělá ideální místo na romantický pobyt.',
        'Glampingy mají často jen jednu nebo dvě jednotky, takže se rychle obsazují. Last-minute termíny jsou šance zažít netradiční ubytování i bez plánování na měsíce dopředu.',
      ],
    },
  },
  {
    slug: 'apartmany',
    kind: 'category',
    h1: 'Apartmány',
    linkLabel: 'Apartmány',
    metaTitle: 'Apartmány – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Apartmán dá vlastní zázemí s kuchyní i soukromím, ať jedete do hor nebo do města. Vyberte si z aktuálních last-minute apartmánů a bydlete po svém.',
    filter: { category: 'apartman' },
    about: {
      heading: 'Apartmány na last-minute pobyt',
      paragraphs: [
        'Apartmán je dobrá volba, když chcete vlastní kuchyň a soukromí, ale nepotřebujete celý dům. Hodí se pro páry i menší rodiny a často je přímo v horském středisku nebo ve městě, takže máte vše po ruce.',
        'Mezi apartmány najdete ubytování v rekonstruovaných domech, penzionech i moderních rezidencích. U každého uvidíte, kolik má ložnic, jaké je vybavení kuchyně a jestli je k dispozici parkování nebo wellness.',
        'Last-minute apartmány se objevují průběžně po celém Česku. Vyberte si region a termín a vyrazte bez dlouhého plánování.',
      ],
    },
  },
  {
    slug: 'vily',
    kind: 'category',
    h1: 'Vily',
    linkLabel: 'Vily',
    metaTitle: 'Vily – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Prostorná vila je skvělá pro větší skupiny a oslavy – hodně místa, soukromí a často i wellness. Mezi právě uvolněnými termíny najdete vily pro pohodovou společnou dovolenou.',
    filter: { category: 'vila' },
    about: {
      heading: 'Vila pro větší skupinu',
      paragraphs: [
        'Vila je ideální pro oslavy, rodinná setkání nebo firemní akce – hodně místa, několik koupelen, prostorný obývák a často i wellness nebo bazén. Celá skupina je pohromadě a nikdo nemusí řešit, kdo bydlí kde.',
        'Při výběru se podívejte na počet ložnic a lůžek, pravidla pro oslavy a hluk a na vybavení kuchyně. U větších skupin se vyplatí zkontrolovat i parkování.',
        'Termíny ve vilách se uvolňují méně často, protože si je skupiny rezervují dlouho dopředu. Když se nějaký objeví, bývá to výborná příležitost – ukazujeme ho tu hned.',
      ],
    },
  },
  {
    slug: 'roubenky',
    kind: 'category',
    h1: 'Roubenky',
    linkLabel: 'Roubenky',
    metaTitle: 'Roubenky – ubytování na poslední chvíli | Mátevolno',
    perex:
      'Roubenka nabídne kouzlo tradiční dřevěné architektury s moderním komfortem uvnitř. Prohlédněte si last-minute nabídky roubenek a užijte si útulný pobyt s duší.',
    filter: { category: 'roubenka' },
    about: {
      heading: 'Roubenky – tradiční dřevěné chalupy',
      paragraphs: [
        'Roubenka je stavba z dřevěných trámů, typická pro Krkonoše, Jizerské hory, Beskydy nebo Český ráj. Dřevo dává interiéru zvláštní atmosféru a vůni, uvnitř je v létě příjemný chládek a v zimě teplo od kamen.',
        'Dnešní roubenky spojují tradiční vzhled s moderním komfortem – mají vybavené kuchyně, koupelny a často i saunu nebo krb. Některé jsou přes sto let staré a citlivě opravené, jiné jsou novostavby v tradičním stylu.',
        'V nabídce najdete roubenky pro dva i pro velké skupiny. Ukazujeme jen termíny, které jsou právě teď volné, takže si můžete vybrat a vyrazit třeba už tento víkend.',
      ],
    },
  },
]

// Marketing-label pages ("zaměření"). The five segments with real search
// demand per the July 2026 keyword research; the remaining labels (samota,
// výhled, parta, krb) stay filter-only — no direct demand, no page. `labelId`
// values come from `/enum` `labels` and are stable backend ids.
const labelPages: LandingPage[] = [
  {
    slug: 'romanticke-pobyty',
    kind: 'label',
    h1: 'Romantické pobyty pro dva',
    linkLabel: 'Romantické pobyty',
    metaTitle: 'Romantické pobyty pro dva – last-minute | Mátevolno',
    perex:
      'Útěk ve dvou bez plánování dopředu – chalupy se soukromím, vířivky pod hvězdami a rána, kdy nikam nespěcháte. Vybírejte z právě uvolněných romantických pobytů a překvapte třeba už tento víkend.',
    filter: { labelId: 4 },
    about: {
      heading: 'Romantický pobyt pro dva bez plánování',
      paragraphs: [
        'Na romantický pobyt nepotřebujete hotel s menu o pěti chodech. Stačí místo, kde jste sami: chalupa na samotě, glamping s výhledem nebo domek s vířivkou na terase, kde vás nikdo neruší.',
        'Mezi romantickými pobyty vybíráme ubytování, která se hodí pro dva – menší, útulná, často s krbem, saunou nebo kádí. V popisu najdete, co je v ceně, jak probíhá příjezd a jestli je v okolí restaurace, nebo si budete vařit sami.',
        'Last-minute termíny jsou ideální na překvapení: na výročí, narozeniny nebo jen tak. Rezervaci vyřídíte online během pár minut.',
      ],
    },
  },
  {
    slug: 'wellness-pobyty',
    kind: 'label',
    h1: 'Wellness pobyty',
    linkLabel: 'Wellness pobyty',
    metaTitle: 'Wellness pobyty – sauna a vířivka last-minute | Mátevolno',
    perex:
      'Privátní sauna, vířivka nebo horká káď – wellness, které nemusíte s nikým sdílet. Prohlédněte si právě uvolněné wellness pobyty na chalupách i v penzionech a zrelaxujte na poslední chvíli.',
    filter: { labelId: 1 },
    about: {
      heading: 'Wellness pobyt v soukromí',
      paragraphs: [
        'Wellness nemusí znamenat hotel plný lidí a rezervaci saunového slotu. Na chalupách a v menších ubytováních máte saunu, vířivku nebo káď jen pro sebe a odpočíváte podle sebe.',
        'Vybírat můžete z finských saun, infrasaun, venkovních vířivek i dřevěných kádí, které se roztápí ohněm. V popisu zkontrolujte, co je v ceně a co se doplácí, a jestli je wellness k dispozici po celou dobu pobytu.',
        'Wellness pobyty jsou nejžádanější od podzimu do jara. Sledujte last-minute nabídky – majitelé sem přidávají volné termíny průběžně a často i na nejbližší víkend.',
      ],
    },
  },
  {
    slug: 'pobyty-na-horach',
    kind: 'label',
    h1: 'Pobyty na horách',
    linkLabel: 'Pobyty na horách',
    metaTitle: 'Pobyty na horách – last-minute ubytování | Mátevolno',
    perex:
      'Hřebenovky, čistý vzduch a večer klid horské chalupy. Vybírejte z právě uvolněných pobytů na horách od Krkonoš po Beskydy a vyrazte bez dlouhého plánování.',
    filter: { labelId: 8 },
    about: {
      heading: 'Pobyt na horách na poslední chvíli',
      paragraphs: [
        'Na hory se dá vyrazit kdykoli: v zimě na lyže a běžky, v létě na hřebenovky a kolo, na podzim za barvami a klidem. V Česku máte na výběr od Krkonoš a Šumavy po Jeseníky a Beskydy – každé hory mají jinou atmosféru.',
        'Mezi horskými pobyty najdete chalupy, roubenky, chaty i apartmány. Pokud jedete lyžovat, podívejte se na vzdálenost od sjezdovky nebo stopy, v létě zase na výchozí body túr a parkování.',
        'Horské ubytování se v sezoně obsazuje dlouho dopředu, ale termíny se uvolňují. Ukazujeme jen ty, které jsou právě teď volné, takže si můžete naplánovat hory třeba na tento víkend.',
      ],
    },
  },
  {
    slug: 'pobyty-u-vody',
    kind: 'label',
    h1: 'Pobyty u vody',
    linkLabel: 'Pobyty u vody',
    metaTitle: 'Pobyty u vody – last-minute ubytování | Mátevolno',
    perex:
      'Ráno skočit do rybníka, večer grilovat na břehu. Sledujte právě uvolněné termíny chat a chalup u vody – od jihočeských rybníků po přehrady a řeky.',
    filter: { labelId: 9 },
    about: {
      heading: 'Pobyt u vody – rybníky, řeky, přehrady',
      paragraphs: [
        'Ubytování u vody je v létě nejžádanější: ráno skok do rybníka, přes den paddleboard nebo loďka, večer gril na břehu. Mimo sezonu zase klid, mlhy nad hladinou a procházky kolem vody.',
        'Najdete tu chaty a chalupy u jihočeských rybníků, u přehrad jako Lipno, Slapy nebo Orlík i u řek, kde se dá splouvat. V popisu zkontrolujte, jak daleko je voda, jestli je přístup soukromý a zda se dá půjčit loďka nebo kola.',
        'Termíny u vody mizí v létě nejrychleji. Sledujte last-minute nabídky a vyrazte, jakmile se něco uvolní.',
      ],
    },
  },
  {
    slug: 'rodinne-pobyty',
    kind: 'label',
    h1: 'Rodinné pobyty',
    linkLabel: 'Rodinné pobyty',
    metaTitle: 'Rodinné pobyty s dětmi – last-minute | Mátevolno',
    perex:
      'Zahrada, hřiště a dost místa, aby se děti vyřádily a vy si konečně odpočinuli. Vybírejte z právě uvolněných rodinných pobytů a vyrazte třeba už tento víkend.',
    filter: { labelId: 5 },
    about: {
      heading: 'Rodinný pobyt s dětmi na poslední chvíli',
      paragraphs: [
        'Rodinný pobyt potřebuje hlavně prostor: dost postelí, zahradu, kde se děti vyřádí, a kuchyň, ve které uvaříte jídlo, které všichni snědí. Ideální je chalupa nebo chata celá pro vás, kde nikoho nerušíte ani v sedm ráno.',
        'Mezi rodinnými pobyty vybíráme ubytování s vybavením pro děti – postýlkou, židličkou, hřištěm nebo oploceným pozemkem. V popisu každého místa uvidíte, kolik má ložnic a co všechno je k dispozici.',
        'S dětmi se plánuje těžko dopředu, a proto se hodí last-minute termíny: když je hezky a všichni zdraví, vyberete ubytování a vyrazíte.',
      ],
    },
  },
]

const basePages: LandingPage[] = [...regionPages, ...facilityPages, ...categoryPages, ...labelPages]

/**
 * Single-dimension, hand-authored landing pages (region / facility / category).
 * The combo layer (`combos.ts`) composes region×dimension pages from these.
 */
export function getBasePages(): LandingPage[] {
  return basePages
}

export function getBasePageBySlug(slug: string): LandingPage | undefined {
  return basePages.find((page) => page.slug === slug)
}

/** All landing pages: hand-authored bases + generated region×dimension combos. */
export function getLandingPages(): LandingPage[] {
  return [...basePages, ...getComboPages()]
}

/**
 * Root-relative URL path for a landing page. Single-dimension pages live at
 * `/pobyty/<slug>` (`/pobyty/sumava`, `/pobyty/se-saunou`); combos are nested
 * region-first (`/pobyty/jizni-cechy/s-krbem`) — the e-chalupy.cz style,
 * kept under the `/pobyty` prefix to avoid a root-level catch-all route.
 */
export function landingPath(page: LandingPage): string {
  if (page.kind === 'combo' && page.comboRegion && page.comboBase) {
    return `/pobyty/${page.comboRegion}/${page.comboBase}`
  }
  return `/pobyty/${page.slug}`
}

/**
 * Short label for a page in a breadcrumb trail — the plain region/facility/type
 * name (e.g. "Krkonoše", "Se saunou"), not the full marketing H1. For a combo
 * it's the dimension (the region is already an ancestor crumb).
 */
export function breadcrumbLabel(page: LandingPage): string {
  if (page.kind === 'combo' && page.comboBase) {
    return getBasePageBySlug(page.comboBase)?.linkLabel ?? page.linkLabel
  }
  return page.linkLabel
}
